import { Component, ComponentRef, inject, DestroyRef, OnInit, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FIELD_EXTRA_DATA } from '@app/core/model/other/field-config';
import { FieldGroupAnchorDirective } from '@app/shared/components/field-groups/field-group-anchor.directive';
import { AbstractFieldGroupBuilder } from '@app/shared/components/fields/abstract.field';
import { FormStateService } from '@app/shared/components/form-builder/form-state.service';
import { AppManager } from '@services/managers/app.manager';
import { distinctUntilChanged } from 'rxjs/operators';

@Component({
  selector: 'section-builder',
  templateUrl: './section-builder.component.html',
  styleUrls: ['./section-builder.component.scss'],
  standalone: false
})
export class SectionBuilderComponent implements OnInit {
  public empty: boolean = true;
  public expanded: boolean = false;
  public fieldGroupRefs: ComponentRef<AbstractFieldGroupBuilder>[] = [];
  private sessionStorageKey: string;

  @ViewChild(FieldGroupAnchorDirective, {static: true}) public fieldGroupHost: FieldGroupAnchorDirective;

  private formStateService: FormStateService = inject(FormStateService);
  private appManager: AppManager = inject(AppManager);
  private destroyRef = inject(DestroyRef);
  protected data = inject<{
    sectionLabel: string,
    sectionEmptyLabel: string,
    displayHeader: string
  }>(FIELD_EXTRA_DATA);

  /**
   * Display an empty section label if the section contains only computed fields and all of them have no value
   */
  public ngOnInit(): void {
    // Set expanded value according to the value saved in session storage.
    // If no value is saved in session storage and the header is displayed, then the section is closed by default.
    // Otherwise, if the header is not displayed, the section must be open.
    this.sessionStorageKey = `section-${this.appManager.currentEntity.objectType}-${this.data.sectionLabel}-${this.appManager.currentOrganization.id}`;
    const sessionStorageValue = sessionStorage.getItem(this.sessionStorageKey);
    this.expanded = sessionStorageValue ? JSON.parse(sessionStorageValue) : !this.data.displayHeader;

    this.fieldGroupRefs.forEach(fieldGroup => {
      this.formStateService.getPath([fieldGroup.instance.fieldGroup.code, 'isDisplayed'])
        ?.pipe(distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
        ?.subscribe((isDisplayed: boolean) => {
          this.empty = this.empty && !isDisplayed;
        });
    });
  }

  /**
   * Save current expanded state of the section in session storage
   * @param isOpen Whether to section is opened or closed.
   */
  public saveExpandedState(isOpen: boolean): void {
    sessionStorage.setItem(this.sessionStorageKey, JSON.stringify(isOpen));
  }
}
